import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Settings as SettingsIcon,
  Palette,
  Shield,
  Bell,
  Key,
  Check,
  Plus,
  Trash2,
  Copy,
  Eye,
  EyeOff,
  AlertTriangle,
  Monitor,
  Smartphone,
  Tablet,
  MapPin,
  Clock,
  LogOut,
  Mail,
  MessageSquare,
  DollarSign,
  Moon,
  Megaphone,
  AlertCircle,
  Calendar,
  Activity,
  X,
} from 'lucide-react';
import {
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  CardDescription,
  Button,
  Input,
  Modal,
  Badge,
} from '../components/ui';
import { useThemeStore } from '../stores/theme.store';
import { useAuthStore } from '../stores/auth.store';
import { themes } from '../config/theme';
import { cn } from '../lib/utils';
import { api } from '../lib/api';

// Types
interface ApiKey {
  id: string;
  name: string;
  key: string;
  permissions: string[];
  lastUsedAt?: string;
  expiresAt?: string;
  createdAt: string;
  status: 'ACTIVE' | 'EXPIRED' | 'REVOKED';
}

interface Session {
  id: string;
  deviceName: string;
  deviceType: 'MOBILE' | 'DESKTOP' | 'TABLET' | 'UNKNOWN';
  ipAddress: string;
  location?: string;
  lastActivity: string;
  createdAt: string;
  isCurrent: boolean;
}

interface LoginActivity {
  id: string;
  status: 'SUCCESS' | 'FAILED' | 'BLOCKED';
  deviceName?: string;
  deviceType?: string;
  ipAddress: string;
  location?: string;
  failReason?: string;
  createdAt: string;
}

interface SecurityPreferences {
  twoFactorEnabled: boolean;
  highValueConfirmation: boolean;
  highValueThreshold: number;
  loginAlerts: boolean;
  sessionTimeoutEnabled: boolean;
  sessionTimeoutMinutes: number;
}

interface NotificationPreferences {
  emailEnabled: boolean;
  pushEnabled: boolean;
  smsEnabled: boolean;
  inAppEnabled: boolean;
  transactionAlerts: boolean;
  securityAlerts: boolean;
  marketingEmails: boolean;
  systemNotifications: boolean;
  transactionThreshold: number;
  quietHoursEnabled: boolean;
  quietHoursStart?: string;
  quietHoursEnd?: string;
}

interface TwoFactorSetup {
  secret: string;
  qrCodeUrl: string;
  manualEntryKey: string;
}

interface ToggleItemProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  checked: boolean;
  onChange: () => void;
  disabled?: boolean;
  badge?: string;
}

// ToggleItem Component
function ToggleItem({ icon, title, description, checked, onChange, disabled, badge }: ToggleItemProps) {
  const handleClick = () => {
    if (!disabled) {
      onChange();
    }
  };

  return (
    <div 
      onClick={handleClick}
      className={cn(
        'flex items-center justify-between p-4 rounded-lg bg-surface-800/50',
        disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer hover:bg-surface-800'
      )}
    >
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-primary-500/20 flex items-center justify-center text-primary-400">
          {icon}
        </div>
        <div>
          <div className="flex items-center gap-2">
            <p className="text-sm font-medium text-white">{title}</p>
            {badge && (
              <span className="px-2 py-0.5 text-xs bg-primary-500/20 text-primary-300 rounded-full">
                {badge}
              </span>
            )}
          </div>
          <p className="text-xs text-surface-400">{description}</p>
        </div>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={(e) => {
          e.stopPropagation();
          handleClick();
        }}
        className={cn(
          'relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-surface-900',
          checked ? 'bg-primary-500' : 'bg-surface-600',
          disabled ? 'cursor-not-allowed' : 'cursor-pointer'
        )}
      >
        <span
          className={cn(
            'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
            checked ? 'translate-x-5' : 'translate-x-0'
          )}
        />
      </button>
    </div>
  );
}

// Available permissions for API Keys
const AVAILABLE_PERMISSIONS = [
  { id: 'tenants:read', label: 'Ler Tenants', category: 'Tenants' },
  { id: 'tenants:write', label: 'Criar/Editar Tenants', category: 'Tenants' },
  { id: 'users:read', label: 'Ler Usuários', category: 'Usuários' },
  { id: 'users:write', label: 'Criar/Editar Usuários', category: 'Usuários' },
  { id: 'transactions:read', label: 'Ler Transações', category: 'Transações' },
  { id: 'transactions:write', label: 'Criar Transações', category: 'Transações' },
  { id: 'accounts:read', label: 'Ler Contas', category: 'Contas' },
  { id: 'accounts:write', label: 'Criar/Editar Contas', category: 'Contas' },
  { id: 'reports:read', label: 'Ler Relatórios', category: 'Relatórios' },
  { id: 'settings:read', label: 'Ler Configurações', category: 'Configurações' },
  { id: 'settings:write', label: 'Editar Configurações', category: 'Configurações' },
];


export function Settings() {
  const queryClient = useQueryClient();
  const { currentTheme, setTheme, isDarkMode, toggleDarkMode } = useThemeStore();
  useAuthStore(); // For future use with user context
  const [activeTab, setActiveTab] = useState('appearance');

  // Security states
  const [showRevokeModal, setShowRevokeModal] = useState(false);
  const [selectedSession, setSelectedSession] = useState<Session | null>(null);
  const [show2FASetupModal, setShow2FASetupModal] = useState(false);
  const [show2FADisableModal, setShow2FADisableModal] = useState(false);
  const [twoFACode, setTwoFACode] = useState('');
  const [disablePassword, setDisablePassword] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [showBackupCodes, setShowBackupCodes] = useState(false);
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [copiedCode, setCopiedCode] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // API Keys states
  const [showCreateKeyModal, setShowCreateKeyModal] = useState(false);
  const [showRevokeKeyModal, setShowRevokeKeyModal] = useState(false);
  const [selectedApiKey, setSelectedApiKey] = useState<ApiKey | null>(null);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyPermissions, setNewKeyPermissions] = useState<string[]>([]);
  const [newKeyExpiration, setNewKeyExpiration] = useState('30');
  const [showNewKey, setShowNewKey] = useState(false);
  const [createdKey, setCreatedKey] = useState('');
  const [visibleKeys, setVisibleKeys] = useState<Set<string>>(new Set());

  // Notification states
  const [hasNotificationChanges, setHasNotificationChanges] = useState(false);
  const [localNotifPrefs, setLocalNotifPrefs] = useState<NotificationPreferences | null>(null);

  const tabs = [
    { id: 'appearance', label: 'Aparência', icon: Palette },
    { id: 'security', label: 'Segurança', icon: Shield },
    { id: 'notifications', label: 'Notificações', icon: Bell },
    { id: 'api', label: 'API Keys', icon: Key },
  ];

  // ============ QUERIES ============

  // Fetch API Keys
  const { data: apiKeysData, isLoading: apiKeysLoading } = useQuery({
    queryKey: ['settings', 'api-keys'],
    queryFn: async () => {
      const response = await api.get('/user-settings/api-keys');
      return response.data as { keys: ApiKey[] };
    },
    enabled: activeTab === 'api',
  });

  // Fetch active sessions
  const { data: sessionsData, isLoading: sessionsLoading } = useQuery({
    queryKey: ['settings', 'security', 'sessions'],
    queryFn: async () => {
      const response = await api.get('/user-settings/sessions');
      return response.data as { sessions: Session[] };
    },
    enabled: activeTab === 'security',
  });

  // Fetch login activity
  const { data: activityData, isLoading: activityLoading } = useQuery({
    queryKey: ['settings', 'security', 'activity'],
    queryFn: async () => {
      const response = await api.get('/user-settings/login-history');
      return response.data as { activities: LoginActivity[] };
    },
    enabled: activeTab === 'security',
  });

  // Fetch security preferences
  const { data: securityPrefs, isLoading: securityPrefsLoading } = useQuery({
    queryKey: ['settings', 'security', 'preferences'],
    queryFn: async () => {
      const response = await api.get('/user-settings/security');
      return response.data as SecurityPreferences;
    },
    enabled: activeTab === 'security',
  });

  // Fetch notification preferences
  const { data: notificationPrefs, isLoading: notificationPrefsLoading } = useQuery({
    queryKey: ['settings', 'notifications'],
    queryFn: async () => {
      const response = await api.get('/user-settings/notifications');
      return response.data as NotificationPreferences;
    },
    enabled: activeTab === 'notifications',
  });

  // Initialize local notification state
  useEffect(() => {
    if (notificationPrefs && !localNotifPrefs) {
      setLocalNotifPrefs(notificationPrefs);
    }
  }, [notificationPrefs, localNotifPrefs]);

  // ============ MUTATIONS ============

  // Change password mutation
  const changePasswordMutation = useMutation({
    mutationFn: async (data: { currentPassword: string; newPassword: string }) => {
      await api.put('/user-settings/password', data);
    },
    onSuccess: () => {
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    },
  });

  // Setup 2FA mutation
  const setup2FAMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post('/user-settings/security/2fa/enable');
      return response.data as TwoFactorSetup;
    },
    onSuccess: () => {
      setShow2FASetupModal(true);
    },
  });

  // Verify 2FA mutation
  const verify2FAMutation = useMutation({
    mutationFn: async (code: string) => {
      const response = await api.post('/user-settings/security/2fa/verify', { code });
      return response.data as { backupCodes: string[] };
    },
    onSuccess: (data) => {
      setBackupCodes(data.backupCodes);
      setShowBackupCodes(true);
      setShow2FASetupModal(false);
      setTwoFACode('');
      queryClient.invalidateQueries({ queryKey: ['settings', 'security', 'preferences'] });
    },
  });

  // Disable 2FA mutation
  const disable2FAMutation = useMutation({
    mutationFn: async (data: { password: string; code: string }) => {
      await api.post('/user-settings/security/2fa/disable', data);
    },
    onSuccess: () => {
      setShow2FADisableModal(false);
      setDisablePassword('');
      setDisableCode('');
      queryClient.invalidateQueries({ queryKey: ['settings', 'security', 'preferences'] });
    },
  });

  // Revoke session mutation
  const revokeSessionMutation = useMutation({
    mutationFn: async (sessionId: string) => {
      await api.delete(`/user-settings/sessions/${sessionId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'security', 'sessions'] });
      setShowRevokeModal(false);
      setSelectedSession(null);
    },
  });

  // Revoke all sessions mutation
  const revokeAllSessionsMutation = useMutation({
    mutationFn: async () => {
      await api.delete('/user-settings/sessions');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'security', 'sessions'] });
    },
  });

  // Update security preference mutation
  const updateSecurityPrefMutation = useMutation({
    mutationFn: async (data: Partial<SecurityPreferences>) => {
      const response = await api.put('/user-settings/security', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'security', 'preferences'] });
    },
  });

  // Update notification preferences mutation
  const updateNotificationMutation = useMutation({
    mutationFn: async (data: Partial<NotificationPreferences>) => {
      const response = await api.put('/user-settings/notifications', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'notifications'] });
      setHasNotificationChanges(false);
    },
  });

  // Create API Key mutation
  const createApiKeyMutation = useMutation({
    mutationFn: async (data: { name: string; permissions: string[]; expiresInDays?: number }) => {
      const response = await api.post('/user-settings/api-keys', data);
      return response.data as { key: ApiKey; plainKey: string };
    },
    onSuccess: (data) => {
      setCreatedKey(data.plainKey);
      setShowNewKey(true);
      setShowCreateKeyModal(false);
      setNewKeyName('');
      setNewKeyPermissions([]);
      setNewKeyExpiration('30');
      queryClient.invalidateQueries({ queryKey: ['settings', 'api-keys'] });
    },
  });

  // Revoke API Key mutation
  const revokeApiKeyMutation = useMutation({
    mutationFn: async (keyId: string) => {
      await api.delete(`/user-settings/api-keys/${keyId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'api-keys'] });
      setShowRevokeKeyModal(false);
      setSelectedApiKey(null);
    },
  });


  // ============ HELPERS ============

  const formatDate = (date: string) => {
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(date));
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value / 100);
  };

  const getDeviceIcon = (deviceType?: string) => {
    switch (deviceType?.toUpperCase()) {
      case 'MOBILE':
        return <Smartphone size={20} className="text-primary-400" />;
      case 'TABLET':
        return <Tablet size={20} className="text-primary-400" />;
      default:
        return <Monitor size={20} className="text-primary-400" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'SUCCESS':
      case 'ACTIVE':
        return 'text-green-400';
      case 'FAILED':
      case 'REVOKED':
        return 'text-red-400';
      case 'BLOCKED':
      case 'EXPIRED':
        return 'text-yellow-400';
      default:
        return 'text-surface-400';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'SUCCESS':
        return <Check size={16} className="text-green-400" />;
      case 'FAILED':
        return <X size={16} className="text-red-400" />;
      case 'BLOCKED':
        return <AlertTriangle size={16} className="text-yellow-400" />;
      default:
        return null;
    }
  };

  const handleCopyKey = (key: string) => {
    navigator.clipboard.writeText(key);
    setCopiedCode(true);
    setTimeout(() => setCopiedCode(false), 2000);
  };

  const toggleKeyVisibility = (keyId: string) => {
    const newVisible = new Set(visibleKeys);
    if (newVisible.has(keyId)) {
      newVisible.delete(keyId);
    } else {
      newVisible.add(keyId);
    }
    setVisibleKeys(newVisible);
  };

  const handleNotificationToggle = (key: keyof NotificationPreferences) => {
    if (!localNotifPrefs) return;
    if (key === 'securityAlerts') return; // Cannot disable
    setLocalNotifPrefs({ ...localNotifPrefs, [key]: !localNotifPrefs[key] });
    setHasNotificationChanges(true);
  };

  const handleNotificationSave = () => {
    if (!localNotifPrefs) return;
    updateNotificationMutation.mutate(localNotifPrefs);
  };

  const handleNotificationReset = () => {
    if (notificationPrefs) {
      setLocalNotifPrefs(notificationPrefs);
      setHasNotificationChanges(false);
    }
  };

  const togglePermission = (permId: string) => {
    if (newKeyPermissions.includes(permId)) {
      setNewKeyPermissions(newKeyPermissions.filter(p => p !== permId));
    } else {
      setNewKeyPermissions([...newKeyPermissions, permId]);
    }
  };


  // ============ RENDER ============

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="space-y-6"
    >
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-surface-100 flex items-center gap-3">
          <SettingsIcon className="w-7 h-7 text-primary-400" />
          Configurações
        </h1>
        <p className="text-surface-400 mt-1">
          Personalize sua experiência e configure integrações
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Sidebar */}
        <Card className="lg:col-span-1 h-fit">
          <CardBody className="p-2">
            <nav className="space-y-1">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={cn(
                    'w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors',
                    activeTab === tab.id
                      ? 'bg-primary-500/10 text-primary-400'
                      : 'text-surface-400 hover:text-surface-100 hover:bg-surface-800'
                  )}
                >
                  <tab.icon className="w-5 h-5" />
                  {tab.label}
                </button>
              ))}
            </nav>
          </CardBody>
        </Card>

        {/* Content */}
        <div className="lg:col-span-3 space-y-6">
          {/* ============ APPEARANCE TAB ============ */}
          {activeTab === 'appearance' && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="space-y-6"
            >
              <Card>
                <CardHeader>
                  <CardTitle>Tema</CardTitle>
                  <CardDescription>
                    Escolha o tema que melhor se adapta ao seu estilo
                  </CardDescription>
                </CardHeader>
                <CardBody>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {Object.entries(themes).map(([key, theme]) => (
                      <button
                        key={key}
                        onClick={() => setTheme(key)}
                        className={cn(
                          'relative p-4 rounded-xl border-2 transition-all text-left',
                          currentTheme === key
                            ? 'border-primary-500 bg-primary-500/10'
                            : 'border-surface-700 hover:border-surface-600'
                        )}
                      >
                        {currentTheme === key && (
                          <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-primary-500 flex items-center justify-center">
                            <Check className="w-4 h-4 text-white" />
                          </div>
                        )}
                        <div className="flex items-center gap-3 mb-3">
                          <div
                            className="w-8 h-8 rounded-lg"
                            style={{
                              background: `linear-gradient(135deg, rgb(${theme.colors.primary[500]}), rgb(${theme.colors.accent[500]}))`,
                            }}
                          />
                          <span className="font-medium text-surface-100">
                            {theme.name}
                          </span>
                        </div>
                        <div className="flex gap-1">
                          {[500, 600, 700].map((shade) => (
                            <div
                              key={shade}
                              className="w-6 h-6 rounded"
                              style={{
                                backgroundColor: `rgb(${theme.colors.primary[shade as keyof typeof theme.colors.primary]})`,
                              }}
                            />
                          ))}
                        </div>
                      </button>
                    ))}
                  </div>
                </CardBody>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Modo de Exibição</CardTitle>
                  <CardDescription>
                    Alterne entre modo claro e escuro
                  </CardDescription>
                </CardHeader>
                <CardBody>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-surface-100">Modo Escuro</p>
                      <p className="text-sm text-surface-500">
                        {isDarkMode ? 'Ativado' : 'Desativado'}
                      </p>
                    </div>
                    <button
                      onClick={toggleDarkMode}
                      className={cn(
                        'relative w-14 h-8 rounded-full transition-colors',
                        isDarkMode ? 'bg-primary-500' : 'bg-surface-700'
                      )}
                    >
                      <span
                        className={cn(
                          'absolute top-1 w-6 h-6 rounded-full bg-white transition-transform',
                          isDarkMode ? 'left-7' : 'left-1'
                        )}
                      />
                    </button>
                  </div>
                </CardBody>
              </Card>
            </motion.div>
          )}


          {/* ============ SECURITY TAB ============ */}
          {activeTab === 'security' && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="space-y-6"
            >
              {/* Change Password */}
              <Card>
                <CardHeader>
                  <CardTitle>Alterar Senha</CardTitle>
                  <CardDescription>
                    Atualize sua senha de acesso regularmente para maior segurança
                  </CardDescription>
                </CardHeader>
                <CardBody className="space-y-4">
                  <Input
                    label="Senha Atual"
                    type="password"
                    placeholder="••••••••"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                  />
                  <Input
                    label="Nova Senha"
                    type="password"
                    placeholder="••••••••"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                  />
                  <Input
                    label="Confirmar Nova Senha"
                    type="password"
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                  />
                  {newPassword && confirmPassword && newPassword !== confirmPassword && (
                    <p className="text-sm text-red-400">As senhas não coincidem</p>
                  )}
                  {changePasswordMutation.isSuccess && (
                    <p className="text-sm text-green-400">Senha alterada com sucesso!</p>
                  )}
                  {changePasswordMutation.isError && (
                    <p className="text-sm text-red-400">Erro ao alterar senha. Verifique a senha atual.</p>
                  )}
                  <div className="flex justify-end">
                    <Button
                      variant="primary"
                      onClick={() => changePasswordMutation.mutate({ currentPassword, newPassword })}
                      disabled={!currentPassword || !newPassword || newPassword !== confirmPassword || changePasswordMutation.isPending}
                    >
                      {changePasswordMutation.isPending ? 'Alterando...' : 'Alterar Senha'}
                    </Button>
                  </div>
                </CardBody>
              </Card>

              {/* Two-Factor Authentication */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Key className="w-5 h-5 text-primary-400" />
                    Autenticação em Dois Fatores (2FA)
                  </CardTitle>
                  <CardDescription>
                    Adicione uma camada extra de segurança à sua conta
                  </CardDescription>
                </CardHeader>
                <CardBody>
                  {securityPrefsLoading ? (
                    <div className="h-16 bg-surface-800/50 rounded-lg animate-pulse" />
                  ) : securityPrefs?.twoFactorEnabled ? (
                    <div className="flex items-center justify-between p-4 rounded-lg bg-green-500/10 border border-green-500/20">
                      <div className="flex items-center gap-3">
                        <Check size={24} className="text-green-400" />
                        <div>
                          <p className="text-sm font-medium text-green-400">2FA Ativado</p>
                          <p className="text-xs text-green-300/70">Sua conta está protegida com autenticação de dois fatores</p>
                        </div>
                      </div>
                      <Button variant="danger" size="sm" onClick={() => setShow2FADisableModal(true)}>
                        Desativar
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between p-4 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
                      <div className="flex items-center gap-3">
                        <AlertTriangle size={24} className="text-yellow-400" />
                        <div>
                          <p className="text-sm font-medium text-yellow-400">2FA Desativado</p>
                          <p className="text-xs text-yellow-300/70">Recomendamos ativar para maior segurança</p>
                        </div>
                      </div>
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => setup2FAMutation.mutate()}
                        disabled={setup2FAMutation.isPending}
                      >
                        {setup2FAMutation.isPending ? 'Configurando...' : 'Ativar 2FA'}
                      </Button>
                    </div>
                  )}
                </CardBody>
              </Card>

              {/* Security Preferences */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Shield className="w-5 h-5 text-primary-400" />
                    Preferências de Segurança
                  </CardTitle>
                </CardHeader>
                <CardBody>
                  {securityPrefsLoading ? (
                    <div className="space-y-4">
                      {[1, 2, 3].map((i) => (
                        <div key={i} className="h-16 bg-surface-800/50 rounded-lg animate-pulse" />
                      ))}
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <label className="flex items-center justify-between p-4 rounded-lg bg-surface-800/50 cursor-pointer hover:bg-surface-800">
                        <div>
                          <p className="text-sm font-medium text-white">Confirmação para transações de alto valor</p>
                          <p className="text-xs text-surface-400">
                            Solicitar confirmação adicional para valores acima de {formatCurrency(securityPrefs?.highValueThreshold || 100000)}
                          </p>
                        </div>
                        <input
                          type="checkbox"
                          checked={securityPrefs?.highValueConfirmation ?? true}
                          onChange={(e) => updateSecurityPrefMutation.mutate({ highValueConfirmation: e.target.checked })}
                          className="w-5 h-5 rounded border-surface-600 bg-surface-700 text-primary-500 focus:ring-primary-500"
                        />
                      </label>

                      <label className="flex items-center justify-between p-4 rounded-lg bg-surface-800/50 cursor-pointer hover:bg-surface-800">
                        <div>
                          <p className="text-sm font-medium text-white">Alertas de login</p>
                          <p className="text-xs text-surface-400">Receber notificação quando houver login em novo dispositivo</p>
                        </div>
                        <input
                          type="checkbox"
                          checked={securityPrefs?.loginAlerts ?? true}
                          onChange={(e) => updateSecurityPrefMutation.mutate({ loginAlerts: e.target.checked })}
                          className="w-5 h-5 rounded border-surface-600 bg-surface-700 text-primary-500 focus:ring-primary-500"
                        />
                      </label>

                      <label className="flex items-center justify-between p-4 rounded-lg bg-surface-800/50 cursor-pointer hover:bg-surface-800">
                        <div>
                          <p className="text-sm font-medium text-white">Timeout de sessão</p>
                          <p className="text-xs text-surface-400">
                            Encerrar sessão após {securityPrefs?.sessionTimeoutMinutes || 15} minutos de inatividade
                          </p>
                        </div>
                        <input
                          type="checkbox"
                          checked={securityPrefs?.sessionTimeoutEnabled ?? true}
                          onChange={(e) => updateSecurityPrefMutation.mutate({ sessionTimeoutEnabled: e.target.checked })}
                          className="w-5 h-5 rounded border-surface-600 bg-surface-700 text-primary-500 focus:ring-primary-500"
                        />
                      </label>
                    </div>
                  )}
                </CardBody>
              </Card>

              {/* Active Sessions */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <Monitor className="w-5 h-5 text-primary-400" />
                        Sessões Ativas
                      </CardTitle>
                      <CardDescription>Dispositivos conectados à sua conta</CardDescription>
                    </div>
                    {sessionsData?.sessions && sessionsData.sessions.length > 1 && (
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => revokeAllSessionsMutation.mutate()}
                        disabled={revokeAllSessionsMutation.isPending}
                      >
                        <LogOut size={16} className="mr-2" />
                        Encerrar todas
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardBody>
                  {sessionsLoading ? (
                    <div className="space-y-3">
                      {[1, 2, 3].map((i) => (
                        <div key={i} className="flex items-center gap-4 p-4 rounded-lg bg-surface-800/50 animate-pulse">
                          <div className="w-10 h-10 rounded-full bg-surface-700" />
                          <div className="flex-1">
                            <div className="h-4 w-40 bg-surface-700 rounded mb-2" />
                            <div className="h-3 w-32 bg-surface-700 rounded" />
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : sessionsData?.sessions?.length ? (
                    <div className="space-y-3">
                      {sessionsData.sessions.map((session) => (
                        <div
                          key={session.id}
                          className={cn(
                            'flex items-center gap-4 p-4 rounded-lg',
                            session.isCurrent ? 'bg-primary-500/10 border border-primary-500/30' : 'bg-surface-800/50'
                          )}
                        >
                          <div className="w-10 h-10 rounded-full bg-surface-700 flex items-center justify-center">
                            {getDeviceIcon(session.deviceType)}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <p className="text-sm font-medium text-white">{session.deviceName || 'Dispositivo desconhecido'}</p>
                              {session.isCurrent && (
                                <span className="px-2 py-0.5 text-xs bg-primary-500 text-white rounded-full">Atual</span>
                              )}
                            </div>
                            <div className="flex items-center gap-3 mt-1">
                              <span className="flex items-center gap-1 text-xs text-surface-400">
                                <MapPin size={12} />
                                {session.location || session.ipAddress}
                              </span>
                              <span className="flex items-center gap-1 text-xs text-surface-400">
                                <Clock size={12} />
                                {formatDate(session.lastActivity)}
                              </span>
                            </div>
                          </div>
                          {!session.isCurrent && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setSelectedSession(session);
                                setShowRevokeModal(true);
                              }}
                            >
                              <LogOut size={16} />
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <Monitor size={48} className="mx-auto text-surface-600 mb-3" />
                      <p className="text-surface-400">Nenhuma sessão ativa</p>
                    </div>
                  )}
                </CardBody>
              </Card>

              {/* Login Activity */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Activity className="w-5 h-5 text-primary-400" />
                    Atividade de Login
                  </CardTitle>
                  <CardDescription>Histórico recente de tentativas de login</CardDescription>
                </CardHeader>
                <CardBody>
                  {activityLoading ? (
                    <div className="space-y-3">
                      {[1, 2, 3, 4, 5].map((i) => (
                        <div key={i} className="flex items-center gap-4 p-3 rounded-lg bg-surface-800/50 animate-pulse">
                          <div className="w-8 h-8 rounded-full bg-surface-700" />
                          <div className="flex-1">
                            <div className="h-4 w-32 bg-surface-700 rounded mb-2" />
                            <div className="h-3 w-24 bg-surface-700 rounded" />
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : activityData?.activities?.length ? (
                    <div className="space-y-2">
                      {activityData.activities.slice(0, 10).map((activity) => (
                        <div
                          key={activity.id}
                          className="flex items-center gap-4 p-3 rounded-lg bg-surface-800/50"
                        >
                          <div className="w-8 h-8 rounded-full bg-surface-700 flex items-center justify-center">
                            {getStatusIcon(activity.status)}
                          </div>
                          <div className="flex-1">
                            <p className={cn('text-sm font-medium', getStatusColor(activity.status))}>
                              {activity.status === 'SUCCESS' ? 'Login bem-sucedido' :
                               activity.status === 'FAILED' ? 'Tentativa de login falhou' :
                               'Conta bloqueada temporariamente'}
                            </p>
                            <div className="flex items-center gap-3 mt-1">
                              <span className="text-xs text-surface-400">
                                {activity.deviceName || 'Dispositivo desconhecido'}
                              </span>
                              <span className="text-xs text-surface-500">•</span>
                              <span className="text-xs text-surface-400">{activity.ipAddress}</span>
                            </div>
                          </div>
                          <span className="text-xs text-surface-500">{formatDate(activity.createdAt)}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <Clock size={48} className="mx-auto text-surface-600 mb-3" />
                      <p className="text-surface-400">Nenhuma atividade registrada</p>
                    </div>
                  )}
                </CardBody>
              </Card>
            </motion.div>
          )}


          {/* ============ NOTIFICATIONS TAB ============ */}
          {activeTab === 'notifications' && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="space-y-6"
            >
              {/* Save/Cancel Bar */}
              {hasNotificationChanges && (
                <div className="flex items-center justify-between p-4 rounded-lg bg-primary-500/10 border border-primary-500/30">
                  <p className="text-sm text-primary-300">Você tem alterações não salvas</p>
                  <div className="flex gap-2">
                    <Button variant="secondary" size="sm" onClick={handleNotificationReset}>
                      Cancelar
                    </Button>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={handleNotificationSave}
                      disabled={updateNotificationMutation.isPending}
                    >
                      {updateNotificationMutation.isPending ? 'Salvando...' : 'Salvar Alterações'}
                    </Button>
                  </div>
                </div>
              )}

              {/* Notification Channels */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Bell className="w-5 h-5 text-primary-400" />
                    Canais de Notificação
                  </CardTitle>
                  <CardDescription>Escolha como deseja receber suas notificações</CardDescription>
                </CardHeader>
                <CardBody>
                  {notificationPrefsLoading || !localNotifPrefs ? (
                    <div className="space-y-4">
                      {[1, 2, 3, 4].map((i) => (
                        <div key={i} className="h-16 bg-surface-800/50 rounded-lg animate-pulse" />
                      ))}
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <ToggleItem
                        icon={<Mail size={20} />}
                        title="E-mail"
                        description="Receba notificações por e-mail"
                        checked={localNotifPrefs.emailEnabled}
                        onChange={() => handleNotificationToggle('emailEnabled')}
                      />
                      <ToggleItem
                        icon={<Smartphone size={20} />}
                        title="Push"
                        description="Notificações push no navegador"
                        checked={localNotifPrefs.pushEnabled}
                        onChange={() => handleNotificationToggle('pushEnabled')}
                      />
                      <ToggleItem
                        icon={<MessageSquare size={20} />}
                        title="SMS"
                        description="Receba alertas por SMS"
                        checked={localNotifPrefs.smsEnabled}
                        onChange={() => handleNotificationToggle('smsEnabled')}
                      />
                      <ToggleItem
                        icon={<Monitor size={20} />}
                        title="In-App"
                        description="Notificações dentro do aplicativo"
                        checked={localNotifPrefs.inAppEnabled}
                        onChange={() => handleNotificationToggle('inAppEnabled')}
                      />
                    </div>
                  )}
                </CardBody>
              </Card>

              {/* Notification Types */}
              <Card>
                <CardHeader>
                  <CardTitle>Tipos de Notificação</CardTitle>
                  <CardDescription>Escolha quais tipos de notificação deseja receber</CardDescription>
                </CardHeader>
                <CardBody>
                  {notificationPrefsLoading || !localNotifPrefs ? (
                    <div className="space-y-4">
                      {[1, 2, 3, 4].map((i) => (
                        <div key={i} className="h-16 bg-surface-800/50 rounded-lg animate-pulse" />
                      ))}
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <ToggleItem
                        icon={<DollarSign size={20} />}
                        title="Alertas de Transação"
                        description="Notificações sobre movimentações financeiras"
                        checked={localNotifPrefs.transactionAlerts}
                        onChange={() => handleNotificationToggle('transactionAlerts')}
                      />
                      <ToggleItem
                        icon={<Shield size={20} />}
                        title="Alertas de Segurança"
                        description="Notificações sobre atividades de segurança"
                        checked={localNotifPrefs.securityAlerts}
                        onChange={() => {}}
                        disabled
                        badge="Obrigatório"
                      />
                      <ToggleItem
                        icon={<Megaphone size={20} />}
                        title="E-mails de Marketing"
                        description="Ofertas, promoções e novidades"
                        checked={localNotifPrefs.marketingEmails}
                        onChange={() => handleNotificationToggle('marketingEmails')}
                      />
                      <ToggleItem
                        icon={<AlertCircle size={20} />}
                        title="Notificações do Sistema"
                        description="Atualizações e manutenções programadas"
                        checked={localNotifPrefs.systemNotifications}
                        onChange={() => handleNotificationToggle('systemNotifications')}
                      />
                    </div>
                  )}
                </CardBody>
              </Card>

              {/* Quiet Hours */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Moon className="w-5 h-5 text-primary-400" />
                    Horário de Silêncio
                  </CardTitle>
                  <CardDescription>Pause notificações durante determinados horários (exceto segurança)</CardDescription>
                </CardHeader>
                <CardBody>
                  {notificationPrefsLoading || !localNotifPrefs ? (
                    <div className="h-16 bg-surface-800/50 rounded-lg animate-pulse" />
                  ) : (
                    <div className="space-y-4">
                      <ToggleItem
                        icon={<Moon size={20} />}
                        title="Ativar Horário de Silêncio"
                        description="Pausar notificações não urgentes durante o período definido"
                        checked={localNotifPrefs.quietHoursEnabled}
                        onChange={() => handleNotificationToggle('quietHoursEnabled')}
                      />
                      {localNotifPrefs.quietHoursEnabled && (
                        <div className="grid grid-cols-2 gap-4 mt-4 pl-12">
                          <div>
                            <label className="block text-sm font-medium text-surface-300 mb-2">Início</label>
                            <input
                              type="time"
                              value={localNotifPrefs.quietHoursStart || '22:00'}
                              onChange={(e) => {
                                setLocalNotifPrefs({ ...localNotifPrefs, quietHoursStart: e.target.value });
                                setHasNotificationChanges(true);
                              }}
                              className="w-full px-3 py-2 bg-surface-800 border border-surface-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-surface-300 mb-2">Fim</label>
                            <input
                              type="time"
                              value={localNotifPrefs.quietHoursEnd || '07:00'}
                              onChange={(e) => {
                                setLocalNotifPrefs({ ...localNotifPrefs, quietHoursEnd: e.target.value });
                                setHasNotificationChanges(true);
                              }}
                              className="w-full px-3 py-2 bg-surface-800 border border-surface-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                            />
                          </div>
                        </div>
                      )}
                      <div className="p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/20 mt-4">
                        <div className="flex items-start gap-2">
                          <Shield size={16} className="text-yellow-400 flex-shrink-0 mt-0.5" />
                          <p className="text-xs text-yellow-300/80">
                            Alertas de segurança sempre serão entregues, independente do horário de silêncio.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </CardBody>
              </Card>
            </motion.div>
          )}


          {/* ============ API KEYS TAB ============ */}
          {activeTab === 'api' && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="space-y-6"
            >
              {/* Header with Create Button */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <Key className="w-5 h-5 text-primary-400" />
                        API Keys
                      </CardTitle>
                      <CardDescription>
                        Gerencie suas chaves de API para integrações externas
                      </CardDescription>
                    </div>
                    <Button variant="primary" onClick={() => setShowCreateKeyModal(true)}>
                      <Plus size={16} className="mr-2" />
                      Nova API Key
                    </Button>
                  </div>
                </CardHeader>
                <CardBody>
                  {apiKeysLoading ? (
                    <div className="space-y-3">
                      {[1, 2, 3].map((i) => (
                        <div key={i} className="h-24 bg-surface-800/50 rounded-lg animate-pulse" />
                      ))}
                    </div>
                  ) : apiKeysData?.keys?.length ? (
                    <div className="space-y-3">
                      {apiKeysData.keys.map((apiKey) => (
                        <div
                          key={apiKey.id}
                          className="p-4 rounded-lg bg-surface-800/50 border border-surface-700"
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <p className="font-medium text-white">{apiKey.name}</p>
                                <Badge variant={apiKey.status === 'ACTIVE' ? 'success' : apiKey.status === 'EXPIRED' ? 'warning' : 'error'}>
                                  {apiKey.status === 'ACTIVE' ? 'Ativa' : apiKey.status === 'EXPIRED' ? 'Expirada' : 'Revogada'}
                                </Badge>
                              </div>
                              <div className="flex items-center gap-2 mb-2">
                                <code className="text-sm text-surface-400 font-mono bg-surface-900 px-2 py-1 rounded">
                                  {visibleKeys.has(apiKey.id) ? apiKey.key : '••••••••••••••••••••••••'}
                                </code>
                                <Button variant="ghost" size="sm" onClick={() => toggleKeyVisibility(apiKey.id)}>
                                  {visibleKeys.has(apiKey.id) ? <EyeOff size={14} /> : <Eye size={14} />}
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => handleCopyKey(apiKey.key)}>
                                  <Copy size={14} />
                                </Button>
                              </div>
                              <div className="flex flex-wrap gap-1 mb-2">
                                {apiKey.permissions.slice(0, 3).map((perm) => (
                                  <span key={perm} className="px-2 py-0.5 text-xs bg-surface-700 text-surface-300 rounded">
                                    {perm}
                                  </span>
                                ))}
                                {apiKey.permissions.length > 3 && (
                                  <span className="px-2 py-0.5 text-xs bg-surface-700 text-surface-300 rounded">
                                    +{apiKey.permissions.length - 3} mais
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-4 text-xs text-surface-500">
                                <span className="flex items-center gap-1">
                                  <Calendar size={12} />
                                  Criada em {formatDate(apiKey.createdAt)}
                                </span>
                                {apiKey.lastUsedAt && (
                                  <span className="flex items-center gap-1">
                                    <Activity size={12} />
                                    Último uso: {formatDate(apiKey.lastUsedAt)}
                                  </span>
                                )}
                                {apiKey.expiresAt && (
                                  <span className="flex items-center gap-1">
                                    <Clock size={12} />
                                    Expira em {formatDate(apiKey.expiresAt)}
                                  </span>
                                )}
                              </div>
                            </div>
                            {apiKey.status === 'ACTIVE' && (
                              <Button
                                variant="danger"
                                size="sm"
                                onClick={() => {
                                  setSelectedApiKey(apiKey);
                                  setShowRevokeKeyModal(true);
                                }}
                              >
                                <Trash2 size={14} />
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <Key size={48} className="mx-auto text-surface-600 mb-3" />
                      <p className="text-surface-400 mb-4">Nenhuma API Key criada</p>
                      <Button variant="primary" onClick={() => setShowCreateKeyModal(true)}>
                        <Plus size={16} className="mr-2" />
                        Criar primeira API Key
                      </Button>
                    </div>
                  )}
                </CardBody>
              </Card>

              {/* API Documentation Link */}
              <Card>
                <CardBody className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-white">Documentação da API</p>
                    <p className="text-sm text-surface-400">Acesse a documentação completa da API no Swagger</p>
                  </div>
                  <Button variant="secondary" onClick={() => window.open('http://localhost:7000/docs', '_blank')}>
                    Abrir Swagger
                  </Button>
                </CardBody>
              </Card>
            </motion.div>
          )}
        </div>
      </div>


      {/* ============ MODALS ============ */}

      {/* Revoke Session Modal */}
      <Modal
        isOpen={showRevokeModal}
        onClose={() => setShowRevokeModal(false)}
        title="Encerrar Sessão"
      >
        <div className="space-y-4">
          <div className="p-4 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
            <div className="flex items-start gap-3">
              <AlertTriangle size={20} className="text-yellow-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-yellow-400">Atenção</p>
                <p className="text-xs text-yellow-300/80 mt-1">
                  Ao encerrar esta sessão, o dispositivo será desconectado e precisará fazer login novamente.
                </p>
              </div>
            </div>
          </div>
          {selectedSession && (
            <div className="p-4 rounded-lg bg-surface-800">
              <div className="flex items-center gap-3">
                {getDeviceIcon(selectedSession.deviceType)}
                <div>
                  <p className="text-sm font-medium text-white">{selectedSession.deviceName || 'Dispositivo desconhecido'}</p>
                  <p className="text-xs text-surface-400">{selectedSession.location || selectedSession.ipAddress}</p>
                </div>
              </div>
            </div>
          )}
          <div className="flex gap-3 pt-4">
            <Button variant="secondary" className="flex-1" onClick={() => setShowRevokeModal(false)}>
              Cancelar
            </Button>
            <Button
              variant="danger"
              className="flex-1"
              onClick={() => selectedSession && revokeSessionMutation.mutate(selectedSession.id)}
              disabled={revokeSessionMutation.isPending}
            >
              {revokeSessionMutation.isPending ? 'Encerrando...' : 'Encerrar Sessão'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* 2FA Setup Modal */}
      <Modal
        isOpen={show2FASetupModal}
        onClose={() => setShow2FASetupModal(false)}
        title="Configurar Autenticação de Dois Fatores"
      >
        <div className="space-y-4">
          <p className="text-sm text-surface-300">
            Escaneie o QR Code abaixo com seu aplicativo autenticador (Google Authenticator, Authy, etc.)
          </p>
          {setup2FAMutation.data && (
            <>
              <div className="flex justify-center p-4 bg-white rounded-lg">
                <img src={setup2FAMutation.data.qrCodeUrl} alt="QR Code 2FA" className="w-48 h-48" />
              </div>
              <div className="p-3 rounded-lg bg-surface-800">
                <p className="text-xs text-surface-400 mb-2">Ou insira manualmente:</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-sm text-primary-400 font-mono break-all">
                    {setup2FAMutation.data.manualEntryKey}
                  </code>
                  <Button variant="ghost" size="sm" onClick={() => handleCopyKey(setup2FAMutation.data!.manualEntryKey)}>
                    {copiedCode ? <Check size={16} className="text-green-400" /> : <Copy size={16} />}
                  </Button>
                </div>
              </div>
              <Input
                label="Código de Verificação"
                value={twoFACode}
                onChange={(e) => setTwoFACode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                maxLength={6}
              />
              {verify2FAMutation.isError && (
                <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20">
                  <p className="text-sm text-red-400">Código inválido. Tente novamente.</p>
                </div>
              )}
            </>
          )}
          <div className="flex gap-3 pt-4">
            <Button variant="secondary" className="flex-1" onClick={() => setShow2FASetupModal(false)}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              className="flex-1"
              onClick={() => verify2FAMutation.mutate(twoFACode)}
              disabled={twoFACode.length !== 6 || verify2FAMutation.isPending}
            >
              {verify2FAMutation.isPending ? 'Verificando...' : 'Verificar e Ativar'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Backup Codes Modal */}
      <Modal
        isOpen={showBackupCodes}
        onClose={() => setShowBackupCodes(false)}
        title="Códigos de Backup"
      >
        <div className="space-y-4">
          <div className="p-4 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
            <div className="flex items-start gap-3">
              <AlertTriangle size={20} className="text-yellow-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-yellow-400">Importante!</p>
                <p className="text-xs text-yellow-300/80 mt-1">
                  Guarde estes códigos em um local seguro. Cada código pode ser usado apenas uma vez caso você perca acesso ao seu autenticador.
                </p>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {backupCodes.map((code, index) => (
              <div key={index} className="p-2 bg-surface-800 rounded text-center font-mono text-sm text-white">
                {code}
              </div>
            ))}
          </div>
          <Button variant="primary" className="w-full" onClick={() => setShowBackupCodes(false)}>
            Entendi, guardei os códigos
          </Button>
        </div>
      </Modal>

      {/* Disable 2FA Modal */}
      <Modal
        isOpen={show2FADisableModal}
        onClose={() => setShow2FADisableModal(false)}
        title="Desativar Autenticação de Dois Fatores"
      >
        <div className="space-y-4">
          <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/20">
            <div className="flex items-start gap-3">
              <AlertTriangle size={20} className="text-red-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-red-400">Atenção!</p>
                <p className="text-xs text-red-300/80 mt-1">
                  Desativar o 2FA reduzirá a segurança da sua conta. Você precisará confirmar sua senha e código atual.
                </p>
              </div>
            </div>
          </div>
          <Input
            label="Senha"
            type="password"
            value={disablePassword}
            onChange={(e) => setDisablePassword(e.target.value)}
            placeholder="Digite sua senha"
          />
          <Input
            label="Código 2FA"
            value={disableCode}
            onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder="000000"
            maxLength={6}
          />
          {disable2FAMutation.isError && (
            <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20">
              <p className="text-sm text-red-400">Senha ou código inválido. Tente novamente.</p>
            </div>
          )}
          <div className="flex gap-3 pt-4">
            <Button variant="secondary" className="flex-1" onClick={() => setShow2FADisableModal(false)}>
              Cancelar
            </Button>
            <Button
              variant="danger"
              className="flex-1"
              onClick={() => disable2FAMutation.mutate({ password: disablePassword, code: disableCode })}
              disabled={!disablePassword || disableCode.length !== 6 || disable2FAMutation.isPending}
            >
              {disable2FAMutation.isPending ? 'Desativando...' : 'Desativar 2FA'}
            </Button>
          </div>
        </div>
      </Modal>


      {/* Create API Key Modal */}
      <Modal
        isOpen={showCreateKeyModal}
        onClose={() => setShowCreateKeyModal(false)}
        title="Criar Nova API Key"
      >
        <div className="space-y-4">
          <Input
            label="Nome da API Key"
            value={newKeyName}
            onChange={(e) => setNewKeyName(e.target.value)}
            placeholder="Ex: Integração ERP"
          />
          
          <div>
            <label className="block text-sm font-medium text-surface-300 mb-2">Permissões</label>
            <div className="space-y-2 max-h-48 overflow-y-auto p-3 bg-surface-800 rounded-lg">
              {AVAILABLE_PERMISSIONS.map((perm) => (
                <label key={perm.id} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={newKeyPermissions.includes(perm.id)}
                    onChange={() => togglePermission(perm.id)}
                    className="w-4 h-4 rounded border-surface-600 bg-surface-700 text-primary-500 focus:ring-primary-500"
                  />
                  <span className="text-sm text-surface-300">{perm.label}</span>
                  <span className="text-xs text-surface-500">({perm.category})</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-surface-300 mb-2">Expiração</label>
            <select
              value={newKeyExpiration}
              onChange={(e) => setNewKeyExpiration(e.target.value)}
              className="w-full px-3 py-2 bg-surface-800 border border-surface-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="7">7 dias</option>
              <option value="30">30 dias</option>
              <option value="90">90 dias</option>
              <option value="365">1 ano</option>
              <option value="0">Sem expiração</option>
            </select>
          </div>

          <div className="flex gap-3 pt-4">
            <Button variant="secondary" className="flex-1" onClick={() => setShowCreateKeyModal(false)}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              className="flex-1"
              onClick={() => createApiKeyMutation.mutate({
                name: newKeyName,
                permissions: newKeyPermissions,
                expiresInDays: newKeyExpiration === '0' ? undefined : parseInt(newKeyExpiration),
              })}
              disabled={!newKeyName || newKeyPermissions.length === 0 || createApiKeyMutation.isPending}
            >
              {createApiKeyMutation.isPending ? 'Criando...' : 'Criar API Key'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Revoke API Key Modal */}
      <Modal
        isOpen={showRevokeKeyModal}
        onClose={() => setShowRevokeKeyModal(false)}
        title="Revogar API Key"
      >
        <div className="space-y-4">
          <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/20">
            <div className="flex items-start gap-3">
              <AlertTriangle size={20} className="text-red-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-red-400">Atenção!</p>
                <p className="text-xs text-red-300/80 mt-1">
                  Esta ação é irreversível. Todas as integrações que usam esta chave deixarão de funcionar imediatamente.
                </p>
              </div>
            </div>
          </div>
          {selectedApiKey && (
            <div className="p-4 rounded-lg bg-surface-800">
              <p className="text-sm font-medium text-white">{selectedApiKey.name}</p>
              <code className="text-xs text-surface-400 font-mono">{selectedApiKey.key.slice(0, 20)}...</code>
            </div>
          )}
          <div className="flex gap-3 pt-4">
            <Button variant="secondary" className="flex-1" onClick={() => setShowRevokeKeyModal(false)}>
              Cancelar
            </Button>
            <Button
              variant="danger"
              className="flex-1"
              onClick={() => selectedApiKey && revokeApiKeyMutation.mutate(selectedApiKey.id)}
              disabled={revokeApiKeyMutation.isPending}
            >
              {revokeApiKeyMutation.isPending ? 'Revogando...' : 'Revogar API Key'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* New Key Display Modal */}
      <Modal
        isOpen={showNewKey}
        onClose={() => setShowNewKey(false)}
        title="API Key Criada com Sucesso"
      >
        <div className="space-y-4">
          <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/20">
            <div className="flex items-start gap-3">
              <Check size={20} className="text-green-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-green-400">Sucesso!</p>
                <p className="text-xs text-green-300/80 mt-1">
                  Sua API Key foi criada. Copie-a agora, pois ela não será exibida novamente.
                </p>
              </div>
            </div>
          </div>
          <div className="p-4 rounded-lg bg-surface-800">
            <p className="text-xs text-surface-400 mb-2">Sua nova API Key:</p>
            <div className="flex items-center gap-2">
              <code className="flex-1 text-sm text-primary-400 font-mono break-all bg-surface-900 p-2 rounded">
                {createdKey}
              </code>
              <Button variant="ghost" size="sm" onClick={() => handleCopyKey(createdKey)}>
                {copiedCode ? <Check size={16} className="text-green-400" /> : <Copy size={16} />}
              </Button>
            </div>
          </div>
          <Button variant="primary" className="w-full" onClick={() => setShowNewKey(false)}>
            Entendi, copiei a chave
          </Button>
        </div>
      </Modal>
    </motion.div>
  );
}
